import React from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import { DateTime } from 'luxon';
import i18n from 'i18next';
import { useNavigate } from 'react-router';

import {
  User,
  UnitSystem,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import { uiActions } from 'web-actions';
import { getUiState } from 'web-selectors';
import { Flex, Text } from 'web-styled';
import { data } from 'web-utils';
import {
  Button,
  ClientNotes,
  InviteClient,
} from 'web-components';

import * as S from './styled';

// Types
interface Props {
  id: string;
  clientInfo: User;
}

interface StateProps {
  me: User;
  clientAvatarUrl?: string;
  inviteClientModal: boolean;
}

// Utility functions
const getHeightString = (
  value: string,
  from: UnitSystem,
  to: UnitSystem
) => {
  if (from === 'imperial' && to === 'imperial') {
    return data.inchesToFeetInches(Number(value));
  }

  const converted = data.heightConvert(value, from, to);

  if (converted && typeof converted === 'number') return Math.round(converted)

  if (converted && typeof converted === 'string' && to === 'metric') {
    return Math.round(parseInt(converted))
  }

  return converted
}

const calculateAge = (birthday: string) => {
  if (!birthday) return '-'

  return Math.floor(
    Math.abs(
      DateTime.fromFormat(birthday, 'yyyy-mm-dd').diffNow('years').years
    )
  )
}

// Custom hooks
const useHeaderState = () => {
  return useSelector(
    (state: any): StateProps => ({
      me: coreSelectors.getProfile(state),
      ...getUiState(['clientAvatarUrl', 'inviteClientModal'])(state),
    }),
    shallowEqual
  );
};

const useInviteModal = () => {
  const dispatch = useDispatch();

  const setInviteClientModal = (isOpen: boolean) => {
    dispatch(
      uiActions.setUiState({
        inviteClientModal: isOpen,
      })
    );
  };

  const handleInviteSubmit = (clientInfo: User, values: any, actions: any) => {
    dispatch(
      coreActions.requestInviteClient({
        userId: clientInfo.id,
        payload: values,
        callbacks: {
          ...actions,
          onSuccess: () => {
            setInviteClientModal(false);
          },
        },
      })
    );
  };

  return { setInviteClientModal, handleInviteSubmit };
};

const ClientName: React.FC<{ profile: User['user_profile'] }> = ({ profile }) => (
  <Flex column>
    <Text size="24px" color="#fff" weight={700}>
      {`${profile.first_name || ''} ${profile.last_name || ''}`}
    </Text>
    <Text size="18px" color="#fff" weight={400}>
      {`${profile.location || ''} ${profile.zip || ''}`}
    </Text>
  </Flex>
);

const ClientStats: React.FC<{
  profile: User['user_profile'];
  units: UnitSystem;
  clientUnits: UnitSystem;
}> = ({ profile, units, clientUnits }) => (
  <S.ClientSummary>
    <div className="stat-item">
      <div className="stat-label">GENDER</div>
      <div className="stat-value">
        {profile.gender ? profile.gender === 'male' ? 'M' : 'F' : '-'}
      </div>
      <div className="stat-unit">{profile.gender || '-'}</div>
    </div>
    <div className="stat-item">
      <div className="stat-label">AGE</div>
      <div className="stat-value">{calculateAge(profile.birthday)}</div>
      <div className="stat-unit">years</div>
    </div>
    <div className="stat-item">
      <div className="stat-label">WEIGHT</div>
      <div className="stat-value">
        {profile.weight ? Math.round(+profile.weight) : '-'}
      </div>
      <div className="stat-unit">
        {units === 'imperial' ? 'lbs' : 'kg'}
      </div>
    </div>
    <div className="stat-item">
      <div className="stat-label">HEIGHT</div>
      <div className="stat-value">
        {profile.height ? getHeightString(profile.height, units, clientUnits) : '-'}
      </div>
      <div className="stat-unit">
        {units === 'imperial' ? 'ft’ in”' : 'cm'}
      </div>
    </div >
    <div className="stat-item">
      <div className="stat-label">BODYFAT</div>
      <div className="stat-value">{+profile.bodyfat || '-'}</div>
      <div className="stat-unit">%</div>
    </div>
  </S.ClientSummary >
);

// Main component
const Header: React.FC<Props> = ({ id, clientInfo }) => {
  const navigate = useNavigate();
  const { me, clientAvatarUrl, inviteClientModal } = useHeaderState();
  const { setInviteClientModal, handleInviteSubmit } = useInviteModal();

  const { user_profile: profile, user_settings } = clientInfo;
  const units = me.user_settings.units_system;
  const clientUnits = user_settings.units_system;
  const isOwnProfile = me.id === clientInfo.id;

  const handleEditProfile = () => {
    navigate(`/users/${id}/edit`);
  };

  const handleInvite = () => {
    setInviteClientModal(true);
  };

  const handleInviteClose = () => {
    setInviteClientModal(false);
  };

  const handleInviteSubmitWrapper = (values: any, actions: any) => {
    handleInviteSubmit(clientInfo, values, actions);
  };

  return (
    <>
      <Flex spacing={25} width="100%" background="#666666" padding="50px" justify="space-between">
        <Flex column>
          <S.StyledAvatar src={clientAvatarUrl || profile?.avatar?.large?.url} />
          <Flex spacing={10} column>
            <ClientName profile={profile} />
            {!isOwnProfile && (
              <S.LinkButton onClick={handleInvite}>
                {i18n.t('invite.client')}
              </S.LinkButton>
            )}
          </Flex>
        </Flex>
        <S.CardsBox>
          <S.Card>
            <div className="card-header">
              <h5 className="card-title">Stats</h5>
              <Button ghost appearance="primary" spacing="compact" onClick={handleEditProfile}>
                Edit Profile
              </Button>
            </div>
            <ClientStats
              profile={profile}
              units={units}
              clientUnits={clientUnits}
            />
          </S.Card>
          <ClientNotes clientId={clientInfo.id} />
        </S.CardsBox>
      </Flex>

      <InviteClient
        buttonLabel="Invite"
        email={clientInfo.email}
        isOpen={inviteClientModal}
        onRequestClose={handleInviteClose}
        onSubmit={handleInviteSubmitWrapper}
      />
    </>
  );
};

export default Header;
